import { useCallback, useMemo } from "react";
import { useRouteMatch } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { horseQueryKeys } from "api/query-keys";
import routes from "common/routes";
import { useRacehorse360Api } from "hooks/api";
import { deepObjectUpdater } from "utils/deepObjectUpdater";

type UseGetHorseQueryConfig = Parameters<
  ReturnType<typeof useRacehorse360Api>["useGetHorse"]
>[2];

interface IUseCurrentHorseDetailsParams {
  selectedFields: string[];
  queryConfig?: UseGetHorseQueryConfig;
}

const getHorseQuery = (
  id: string,
  selectedFields: string[]
): racehorse360.IGetHorseRequest | undefined => {
  if (!id) return undefined;

  return {
    id,
    getOptions: {
      select: selectedFields
    }
  };
};

export const useCurrentHorseDetails = ({
  selectedFields,
  queryConfig
}: IUseCurrentHorseDetailsParams) => {
  const match = useRouteMatch<{ horseId: string }>(routes.horseDetails.path);
  const horseId = match?.params.horseId;
  const queryClient = useQueryClient();
  const { useGetHorse } = useRacehorse360Api();

  const queryKey = useMemo(() => horseQueryKeys.detail(horseId), [horseId]);
  const request = useMemo(
    () => getHorseQuery(horseId, selectedFields),
    [horseId, selectedFields]
  );
  const finalQueryConfig = useMemo(
    () => ({
      refetchOnMount: false,
      ...queryConfig
    }),
    [queryConfig]
  );

  const {
    data: currentHorse,
    isLoading: isHorseLoading,
    isFetching: isHorseFetching,
    refetch: refetchHorse,
    compoundRequestKey
  } = useGetHorse(queryKey, request, finalQueryConfig);

  const updateCurrentHorse = useCallback(
    (patch: racehorse360.IHorse) => {
      queryClient.setQueryData(
        compoundRequestKey,
        (oldData: racehorse360.IHorse) => {
          if (!oldData) return oldData;

          return new racehorse360.Horse(deepObjectUpdater(oldData, patch));
        }
      );
    },
    [compoundRequestKey, queryClient]
  );

  return useMemo(
    () => ({
      currentHorse,
      isHorseLoading,
      isHorseFetching,
      refetchHorse,
      compoundRequestKey,
      updateCurrentHorse
    }),
    [
      currentHorse,
      isHorseLoading,
      isHorseFetching,
      refetchHorse,
      compoundRequestKey,
      updateCurrentHorse
    ]
  );
};
